"use client"

import { useState, useEffect } from "react"
import Sidebar from "../components/Sidebar.js"
import { FaEdit, FaTrash } from "react-icons/fa"
import { IoMdAdd, IoMdSearch } from "react-icons/io"
import Modal from "../components/CreateBlogModal.js"
import { fetchBlogs, togglePublish, deleteBlog } from "../comman/Blog.js"

const BlogTable = () => {
  const [search, setSearch] = useState("")
  const [blogs, setBlogs] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [blogToEdit, setBlogToEdit] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  const loadBlogs = async () => {
    try {
      setLoading(true)
      const response = await fetchBlogs()
      setBlogs(response)
      setLoading(false)
    } catch (error) {
      console.error("Error fetching blogs:", error)
      setError("Failed to load blogs. Please try again.")
      setLoading(false)
    }
  }

  useEffect(() => {
    loadBlogs()
  }, []) //Fixed useEffect dependency

  const handleSearch = (e) => {
    setSearch(e.target.value)
  }

  const filteredBlogs = blogs.filter((blog) => blog.title.toLowerCase().includes(search.toLowerCase()))

  const handleTogglePublish = async (blogId) => {
    try {
      await togglePublish(blogId)
      loadBlogs()
    } catch (error) {
      console.error("Error updating blog:", error)
      setError("Failed to update blog. Please try again.")
    }
  }

  const handleDeleteBlog = async (blogId) => {
    if (window.confirm("Are you sure you want to delete this blog?")) {
      try {
        await deleteBlog(blogId)
        loadBlogs()
      } catch (error) {
        console.error("Error deleting blog:", error)
        setError("Failed to delete blog. Please try again.")
      }
    }
  }

  const handleEditBlog = (blog) => {
    setBlogToEdit(blog)
    setIsModalOpen(true)
  }

  const openModal = () => {
    setBlogToEdit(null)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setBlogToEdit(null)
  }

  const handleAddBlog = async () => {
    try {
      await loadBlogs()
      setIsModalOpen(false)
    } catch (err) {
      setError("Error adding blog")
    }
  }

  const handleUpdateBlog = async () => {
    try {
      await loadBlogs()
      setIsModalOpen(false)
    } catch (err) {
      setError("Error updating blog")
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#12143F]"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 md:ml-64 w-full max-w-[100vw] overflow-hidden">
        <div className="max-w-full overflow-hidden">
          <h1 className="text-2xl md:text-3xl font-bold text-[#12143F] mb-6">Blog Management</h1>
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 overflow-hidden">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-[#12143F] focus:border-[#12143F]"
                  placeholder="Search blogs"
                  value={search}
                  onChange={handleSearch}
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <IoMdSearch className="w-5 h-5 text-gray-500" />
                </div>
              </div>
              <button
                onClick={openModal}
                className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-[#12143F] text-white rounded-md hover:bg-opacity-90 transition duration-300"
              >
                <IoMdAdd size={18} className="mr-2" />
                Add Blog
              </button>
            </div>
            <div className="overflow-x-auto -mx-4 md:-mx-6">
              <div className="inline-block min-w-full align-middle p-4 md:p-6">
                <div className="overflow-hidden border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-[#12143F]">
                      <tr>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          No
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Title
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Description
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Category
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Image
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                        >
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredBlogs.map((blog, index) => (
                        <tr key={blog._id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px] truncate">{blog.title}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 max-w-[300px]">
                            <div className="line-clamp-2">{blog.description}</div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">{blog.category}</td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {blog.image && (
                              <img
                                src={blog.image || "/placeholder.svg"}
                                alt={blog.title}
                                className="w-16 h-16 object-cover rounded"
                              />
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => handleEditBlog(blog)}
                                className="text-[#12143F] hover:text-opacity-80 transition-colors duration-200"
                              >
                                <FaEdit size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteBlog(blog._id)}
                                className="text-red-500 hover:text-red-700 transition-colors duration-200"
                              >
                                <FaTrash size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        closeModal={closeModal}
        blogToEdit={blogToEdit}
        onAddBlogs={handleAddBlog}
        onUpdateBlogs={handleUpdateBlog}
      />
    </div>
  )
}

export default BlogTable

